import React, { useMemo, useState, useCallback } from 'react';
import {
  DynamicResultTable,
  HeadCell,
  CustomizeView,
  FormattedDate,
  NoDataContentInTable,
  FormattedAddress,
} from '@css-xp/base';
import { useQuery } from '@apollo/client';
import { fetchCloudSiteProducts } from '../../../queries/fetch-cloud-site-products';
import { CloudChildProduct, NEW_DATE_FORMAT, TRANSACTION_ACCOUNT_PATH_2 } from '@atlassian/cssxp-typescommon';
import { SubscriptionTabProps } from '../../../common-types';
import { DEFAULT_ROWS_PER_PAGE, DEFAULT_PAGE, NO_DETAILS_TO_SHOW } from '@atlassian/cssxp-utils-entitlement';
import { CloudProductDetailView, getBilledForColumnData, isActiveSubscription } from '@css-xp/base/entitlement-utils';
import capitalize from 'lodash/capitalize';
import { CommerceAddress, CreditCard, generatedTypesNew, PaymentMethodType, PaymentOnTerms } from '@css-xp/graphtypes';
import { Link } from 'react-router-dom';
import './subscription-tab.scss';
import { isBilledElsewhereProduct } from '@atlassian/cssxp-utils-product';

const defaultSearchOption = {
  size: DEFAULT_ROWS_PER_PAGE,
  from: 0,
  sort: [
    {
      status: generatedTypesNew.SortDirection.ASC,
    },
    {
      productName: generatedTypesNew.SortDirection.ASC,
    },
  ],
};

const SubscriptionTab = (props: SubscriptionTabProps) => {
  const { parentSen, cloudSiteId } = props;
  const [rowsPerPage, setRowsPerPage] = useState<number>(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState<number>(DEFAULT_PAGE);
  const queryVariables = {
    parentSen: parentSen,
    cloudSiteId: cloudSiteId,
    searchOptions: defaultSearchOption,
    showCcpData: true,
  };

  const { error, loading, data, refetch } = useQuery<
    generatedTypesNew.FetchCloudSiteProductsV2Query,
    generatedTypesNew.FetchCloudSiteProductsV2QueryVariables
  >(fetchCloudSiteProducts, {
    variables: queryVariables,
    displayName: 'fetchCloudSiteProducts',
    nextFetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const getPaymentMethod = (paymentMethod: CreditCard | PaymentOnTerms | null | undefined) => {
    if (paymentMethod) {
      if (paymentMethod?.type === PaymentMethodType.CARD) {
        const creditCard = paymentMethod as CreditCard;
        return <span>{`**** **** **** ${creditCard?.card?.last4Numbers ?? ''}`}</span>;
      } else if (paymentMethod?.type === PaymentMethodType.DEFERRED) {
        const paymentOnTerms = paymentMethod as PaymentOnTerms;
        return <span>Payment on terms: due in {paymentOnTerms.terms?.invoiceDueDays} days</span>;
      }
    }
    return 'Not available';
  };

  const getBillToAddress = (billToParty: CommerceAddress | undefined | null) => {
    if (!billToParty) {
      return 'No address information';
    } else {
      return (
        <span className="line-clamp">
          <FormattedAddress
            addressLine1={billToParty?.addressLine1}
            addressLine2={billToParty?.addressLine2}
            city={billToParty?.city}
            country={billToParty?.country}
            postcode={billToParty?.postcode}
            state={billToParty?.state}
          />
        </span>
      );
    }
  };

  const headData = useMemo(() => {
    const cells = [
      {
        content: 'Subscription',
        width: 15,
      },
      {
        content: 'Billing account',
        width: 15,
      },
      {
        content: 'Billing address',
        width: 15,
      },
      {
        content: 'Payment method',
        width: 10,
        isMandatory: false,
        isChecked: false,
      },
      {
        content: 'Billed for',
        width: 10,
      },
      {
        content: 'Last billed',
        width: 10,
        isMandatory: false,
        isChecked: false,
      },
      {
        content: 'Next bill/ Expiry',
        width: 10,
      },
      {
        content: 'Billing cycle',
        width: 10,
      },
      {
        content: 'Pricing eligibility',
        width: 10,
      },
    ];

    return {
      cells,
    };
  }, []);

  const getCellData = useCallback((header: string, node: CloudChildProduct) => {
    const billedElsewhere = isBilledElsewhereProduct(node?.productId);
    switch (header) {
      case 'Subscription':
        return {
          content: (
            <CloudProductDetailView
              productId={node?.productId}
              entitlementNumber={node?.entitlementNumber || ''}
              billingStatus={node?.billingStatus}
              provisioningStatus={node?.provisioningStatus}
              edition={node?.edition}
              productName={node?.productName}
              trial={node?.trial ?? false}
              isSandbox={node?.isSandbox}
              upgradeTrial={node?.upgradeTrial}
            />
          ),
        };
      case 'Billing account': {
        return {
          content: billedElsewhere ? (
            <div className="opsgenie-billing-account">Billed elsewhere</div>
          ) : (
            <>
              {node?.transactionAccountNumber ? (
                <>
                  <Link
                    to={`${TRANSACTION_ACCOUNT_PATH_2}/${node.transactionAccountNumber}`}
                    className="line-clamp line-clamp-name"
                  >
                    {node?.transactionAccountName ?? 'Unnamed billing account'}
                  </Link>
                  <div>{node.transactionAccountNumber}</div>
                </>
              ) : (
                <NoDataContentInTable />
              )}
            </>
          ),
        };
      }
      case 'Billing address':
        return {
          content: !billedElsewhere && getBillToAddress(node?.billToParty),
        };
      case 'Payment method':
        return {
          content: !billedElsewhere && getPaymentMethod(node?.defaultPaymentMethod),
        };
      case 'Billed for':
        return {
          content: !billedElsewhere && (
            <>
              <div>{capitalize(getBilledForColumnData(node?.productId, node?.edition))}</div>
              <div>{node?.billedUsers ?? '0'}</div>
            </>
          ),
        };
      case 'Last billed':
        return {
          content: !billedElsewhere && (
            <>
              {node?.latestPeriodStartDate ? (
                <FormattedDate date={node?.latestPeriodStartDate} format={NEW_DATE_FORMAT} />
              ) : (
                <NoDataContentInTable />
              )}
            </>
          ),
        };
      case 'Next bill/ Expiry':
        return {
          content: !billedElsewhere && (
            <>
              {node?.latestPeriodEndDate && isActiveSubscription(node?.billingStatus, node?.provisioningStatus) ? (
                <FormattedDate date={node?.latestPeriodEndDate} format={NEW_DATE_FORMAT} />
              ) : (
                <NoDataContentInTable />
              )}
            </>
          ),
        };
      case 'Billing cycle':
        return {
          content: !billedElsewhere && (
            <>{node?.billingCycle ? capitalize(node.billingCycle) : <NoDataContentInTable />}</>
          ),
        };
      case 'Pricing eligibility':
        return {
          content: !billedElsewhere && <>{node?.planType ? capitalize(node.planType) : <NoDataContentInTable />}</>,
        };
      default:
        return {
          content: 'NA',
        };
    }
  }, []);

  const emptyStateProp = {
    header: '',
    description: NO_DETAILS_TO_SHOW,
  };
  const onPageChange = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
      refetch({
        searchOptions: {
          ...defaultSearchOption,
          size: rowsPerPage,
          from: newPage !== 1 ? newPage * rowsPerPage - rowsPerPage : 0,
        },
      });
    },
    [rowsPerPage, refetch]
  );

  const products = data?.cloudSiteProducts?.nodes ?? [];
  const totalCount = data?.cloudSiteProducts?.totalCount ?? 0;

  const handleRowsChange = useCallback(
    (newRowPerPage: number) => {
      if (newRowPerPage === rowsPerPage) {
        return;
      }
      setCurrentPage(1);
      setRowsPerPage(newRowPerPage);
      refetch({
        searchOptions: {
          ...defaultSearchOption,
          size: newRowPerPage,
          from: 0,
        },
      });
    },
    [rowsPerPage, refetch]
  );

  const [columnsList, setColumnList] = useState<{ cells: Array<HeadCell> }>(headData);

  const selectedColumns = useMemo(() => {
    const columns = columnsList.cells.filter(
      // selecting those columns if having key isChecked as true or not having isChecked at all
      (column) => column.isChecked || !Object.prototype.hasOwnProperty.call(column, 'isChecked')
    );
    return { cells: columns };
  }, [columnsList]);

  const rowData = useMemo(() => {
    return products.map((node, index) => {
      return {
        key: node?.id ?? index.toString(),
        // @ts-ignore
        cells: [...selectedColumns.cells.map((columnData) => getCellData(columnData.content as string, node))],
      };
    });
  }, [products, getCellData, selectedColumns]);

  return (
    <div id="cloud-site-products" data-testid="cloud-site-products">
      <DynamicResultTable
        testId={`cloud-site-products`}
        headingText="Subscriptions"
        head={{
          cells: [...selectedColumns.cells],
        }}
        rows={rowData}
        emptyStateProps={emptyStateProp}
        error={error?.toString()}
        isLoading={loading}
        totalCount={totalCount}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        isPaginationEnabled={totalCount > DEFAULT_ROWS_PER_PAGE}
        onRowChange={handleRowsChange}
        customiseColumnView={
          <CustomizeView
            columns={columnsList.cells}
            updatedColumnView={(record: Array<HeadCell>) => setColumnList({ cells: [...record] })}
          />
        }
      ></DynamicResultTable>
    </div>
  );
};

export default React.memo(SubscriptionTab);
